/**
 * お知らせ欄(通知センター・2026-08-26 会議)。
 * ステータス更新時の通知(承認されました・取消されました・支払われました等)を新しい順に表示する。
 * - 各お知らせは管理番号(申請)に紐づき、クリックで該当申請の詳細(申請一覧)へ移動する
 * - 「全て既読」ボタンで未読をまとめて既読にできる
 * - 操作依頼(承認・支払い・受領・完了確認)は従来どおり各ページのバッジにも出る
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { tsToDateLabel } from '@core/lib/date';
import {
  fetchMyNotifications,
  kindOf,
  markAllRead,
  markOneRead,
  markReadForTransaction,
  subscribeMyUnread,
} from './notificationsRepo';
import type { AppNotification } from '@core/lib/types';

/** ページを行き来したとき前回の内容を即表示するためのキャッシュ(2026-08-28) */
const newsCache = new Map<string, AppNotification[]>();

export default function NewsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const cached = user ? newsCache.get(user.id) : undefined;
  const [rows, setRows] = useState<AppNotification[]>(cached ?? []);
  /** 「読み込み中…」は初回(キャッシュなし)だけ。再読込は画面を保ったまま裏で行う */
  const [loading, setLoading] = useState(!cached);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      // お知らせ(info)のみ表示。操作依頼(action)は承認・支払い等の各ページとバッジに出る
      const list = (await fetchMyNotifications(user.id)).filter((n) => kindOf(n) === 'info');
      setRows(list);
      newsCache.set(user.id, list);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'お知らせを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // 新しい通知が届いたら一覧も自動更新する
  useEffect(() => {
    if (!user) return;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 800);
    });
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [user, load]);

  async function readAll() {
    if (!user) return;
    setBusy(true);
    try {
      // お知らせ(info)のみ既読化。操作依頼(承認・支払い等のバッジ)はアクション完了まで残す(2026-08-27)
      await markAllRead(user.id, 'info');
      await load();
      toast('すべてのお知らせを既読にしました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '既読化に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /** 1件だけ既読にする(行の「既読」ボタン・2026-08-27) */
  async function readOne(n: AppNotification) {
    try {
      await markOneRead(n.id);
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
    } catch (e) {
      toast(e instanceof Error ? e.message : '既読化に失敗しました');
    }
  }

  /**
   * クリックで該当申請の詳細へ移動(2026-08-27 変更)。
   * 操作依頼のお知らせ(stageあり)は**アクションを行うページ**(承認/支払い/受領/完了確認)へ遷移し、
   * そこで詳細ポップアップ(操作ボタン付き)を自動で開く。ステータス更新のお知らせは申請一覧の詳細へ
   */
  async function open(n: AppNotification) {
    if (user && !n.read) void markReadForTransaction(user.id, n.transaction_id);
    const to =
      n.stage === 'approve'
        ? '/approve'
        : n.stage === 'pay'
          ? '/pay'
          : n.stage === 'receive'
            ? '/receive-wait'
            : n.stage === 'confirm'
              ? '/confirm'
              : n.stage === 'acct_confirm'
                ? '/acct-confirm'
                : '/mylist';
    navigate(to, { state: { openId: n.transaction_id } });
  }

  const unreadCount = rows.filter((n) => !n.read).length;

  return (
    <section className="screen" style={{ maxWidth: 860 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          お知らせ
          {unreadCount > 0 && <span className="badge b-pend">未読 {unreadCount}件</span>}
          <button
            className="btn-line"
            style={{ marginLeft: 'auto', padding: '5px 14px', fontSize: 12.5 }}
            disabled={busy || unreadCount === 0}
            onClick={() => void readAll()}
          >
            全て既読
          </button>
        </h3>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="empty">お知らせはまだありません。</p>
        ) : (
          <div>
            {rows.map((n) => (
              <div
                key={n.id}
                onClick={() => void open(n)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 8px',
                  borderBottom: '1px solid var(--line)',
                  cursor: 'pointer',
                  background: n.read ? undefined : 'var(--blue-bg)',
                }}
                title="クリックで該当申請の詳細を開きます"
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    marginTop: 6,
                    flexShrink: 0,
                    background: n.read ? 'transparent' : 'var(--red)',
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, overflowWrap: 'anywhere' }}>{n.message}</div>
                  <div className="hint" style={{ fontSize: 11 }}>
                    {tsToDateLabel(n.created_at)}
                  </div>
                </div>
                {!n.read && (
                  <button
                    className="btn-line"
                    style={{ padding: '3px 12px', fontSize: 11.5, flexShrink: 0, alignSelf: 'center' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      void readOne(n);
                    }}
                  >
                    既読
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="hint" style={{ marginTop: 10 }}>
          お知らせをクリックすると該当申請の詳細が開きます。承認・支払いなどの操作依頼は、
          各ページ(承認/支払い/受領/完了確認)のバッジにも表示されます。
        </p>
      </div>
    </section>
  );
}
